export const EVOLUTION_DELETE_WINDOW_MS = 24 * 60 * 60 * 1000;
/** Janela para editar acréscimos e evolução assinada (alinhado ao backend). */
export const EVOLUTION_EDIT_WINDOW_MS = EVOLUTION_DELETE_WINDOW_MS;

/** Mesmo mínimo exigido pelo backend em invalidateEvolution. */
export const EVOLUTION_INVALIDATION_MIN_REASON = 10;
export const EVOLUTION_INVALIDATION_MAX_REASON = 2000;

export type EvolutionRecord = {
  evolution_date?: string | null;
  is_signed?: boolean;
  status?: string;
  signed_at?: string | null;
  professional_id?: string | null;
  professional?: { id?: string | null; full_name?: string | null } | null;
  invalidated_at?: string | null;
  invalidation_reason?: string | null;
  invalidated_by_name?: string | null;
};

export type EvolutionViewer = {
  role?: string | null;
  professional_id?: string | null;
};

export function isEvolutionAdminRole(role?: string | null): boolean {
  const r = String(role || '').toLowerCase();
  return r === 'admin' || r === 'admin_master';
}

export function isEvolutionSigned(evo?: EvolutionRecord | null): boolean {
  return Boolean(evo?.is_signed || evo?.status === 'signed' || evo?.status === 'amended');
}

export function isEvolutionInvalidated(evo?: EvolutionRecord | null): boolean {
  return Boolean(evo?.invalidated_at) || evo?.status === 'cancelled';
}

export function isWithinEvolutionDeleteWindow(signedAt?: string | null, now = Date.now()): boolean {
  if (!signedAt) return false;
  const t = new Date(signedAt).getTime();
  if (Number.isNaN(t)) return false;
  return now - t <= EVOLUTION_DELETE_WINDOW_MS;
}

/** Admin pode excluir rascunho a qualquer momento; assinada só até 24h após signed_at. */
export function canDeleteEvolutionRecord(
  role?: string | null,
  evo?: EvolutionRecord | null,
  now = Date.now(),
): boolean {
  if (!isEvolutionAdminRole(role) || !evo) return false;
  if (isEvolutionInvalidated(evo)) return false;
  if (!isEvolutionSigned(evo)) return true;
  return isWithinEvolutionDeleteWindow(evo.signed_at, now);
}

export function evolutionDeleteTooltip(
  role?: string | null,
  evo?: EvolutionRecord | null,
): string {
  if (!isEvolutionAdminRole(role)) return 'Somente administradores podem excluir evoluções';
  if (!evo) return 'Excluir evolução';
  if (isEvolutionInvalidated(evo)) return 'Evolução invalidada permanece no prontuário como histórico';
  if (!isEvolutionSigned(evo)) return 'Excluir evolução';
  if (isWithinEvolutionDeleteWindow(evo.signed_at)) {
    return 'Excluir evolução assinada (até 24h após a assinatura)';
  }
  return 'Prazo de 24h após assinatura expirado — exclusão não permitida';
}

/** Fim do prazo de edição de uma evolução concluída (null para rascunho). */
export function evolutionEditDeadline(evo?: EvolutionRecord | null): Date | null {
  if (!evo?.signed_at || !isEvolutionSigned(evo)) return null;
  const t = new Date(evo.signed_at).getTime();
  return Number.isNaN(t) ? null : new Date(t + EVOLUTION_EDIT_WINDOW_MS);
}

function evolutionProfessionalId(evo?: EvolutionRecord | null): string | null {
  return evo?.professional_id || evo?.professional?.id || null;
}

/**
 * Edição: somente o profissional que registrou a evolução.
 * Rascunho: sem prazo. Assinada: até 24h após signed_at.
 */
export function canEditEvolutionRecord(
  viewer?: EvolutionViewer | null,
  evo?: EvolutionRecord | null,
  now = Date.now(),
): boolean {
  if (!evo || isEvolutionInvalidated(evo)) return false;
  const authorId = evolutionProfessionalId(evo);
  if (!viewer?.professional_id || !authorId || viewer.professional_id !== authorId) return false;
  if (!isEvolutionSigned(evo)) return true;
  return isWithinEvolutionDeleteWindow(evo.signed_at, now);
}

export function evolutionEditTooltip(
  viewer?: EvolutionViewer | null,
  evo?: EvolutionRecord | null,
): string {
  if (!evo) return 'Editar evolução';
  if (isEvolutionInvalidated(evo)) return 'Evolução invalidada não pode ser editada';
  const authorId = evolutionProfessionalId(evo);
  if (!viewer?.professional_id) return 'Vincule seu usuário a um profissional para editar evoluções';
  if (authorId && viewer.professional_id !== authorId) {
    return 'Somente o profissional que registrou esta evolução pode editá-la';
  }
  if (!isEvolutionSigned(evo)) return 'Editar rascunho da evolução';
  if (isWithinEvolutionDeleteWindow(evo.signed_at)) {
    return 'Editar evolução assinada (até 24h após a assinatura)';
  }
  return 'Prazo de edição de 24h após a assinatura expirou — use «Invalidar» se precisar corrigir';
}

/**
 * Invalidar é a via para corrigir um registro já concluído: sem prazo, aberta a
 * administradores e ao profissional responsável pela evolução.
 */
export function canInvalidateEvolutionRecord(
  viewer?: EvolutionViewer | null,
  evo?: EvolutionRecord | null,
): boolean {
  if (!evo || isEvolutionInvalidated(evo) || !isEvolutionSigned(evo)) return false;
  if (isEvolutionAdminRole(viewer?.role)) return true;
  const professionalId = evolutionProfessionalId(evo);
  return Boolean(viewer?.professional_id && professionalId && viewer.professional_id === professionalId);
}

export function evolutionInvalidateTooltip(
  viewer?: EvolutionViewer | null,
  evo?: EvolutionRecord | null,
): string {
  if (isEvolutionInvalidated(evo)) return 'Evolução já invalidada';
  if (!isEvolutionSigned(evo)) return 'Rascunhos não são invalidados — edite ou exclua a evolução';
  if (!canInvalidateEvolutionRecord(viewer, evo)) {
    return 'Somente administradores ou o profissional responsável podem invalidar';
  }
  return 'Invalidar evolução (exige justificativa)';
}

/** Texto curto de auditoria exibido junto do registro invalidado. */
export function evolutionInvalidationSummary(evo?: EvolutionRecord | null): string {
  if (!isEvolutionInvalidated(evo)) return '';
  const parts: string[] = [];
  if (evo?.invalidated_by_name) parts.push(`por ${evo.invalidated_by_name}`);
  if (evo?.invalidated_at) {
    const d = new Date(evo.invalidated_at);
    if (!Number.isNaN(d.getTime())) parts.push(`em ${d.toLocaleString('pt-BR')}`);
  }
  return parts.length ? `Invalidada ${parts.join(' ')}` : 'Invalidada';
}
